import { useState } from 'react';
import type { MouseEvent, ReactElement } from 'react';
import { Pencil, Plus, Trash2, TriangleAlert, X } from 'lucide-react';

import { useDeleteProject, usePatchProject } from '../api/planning-hooks';
import type { ProjectFieldPatch } from '../api/planning';
import { isConflict } from '../api/planning-hooks';
import { PROJECT_PRIORITIES, PROJECT_PRIORITY_LABELS } from '../api/types';
import type { Assignment, Project, RegistryDocument } from '../api/types';
import { isOverdue } from '../lib/capacity';
import { statusLabel, statusOptions } from '../lib/statuses';
import { priorityRowClass } from '../lib/priority-class';
import { INTERACTIVE, manualRowDragProps } from '../hooks/useManualOrder';
import type { ManualOrderControls } from '../hooks/useManualOrder';
import { useFilters } from '../state/FiltersProvider';
import { ConfirmDialog } from './ConfirmDialog';
import { DatePicker } from './DatePicker';
import { DragHandleCell } from './DragHandleCell';
import { initials, PersonPickerModal } from './PersonPickerModal';

export type { ManualOrderControls };

interface ProjectRowProps {
  project: Project;
  registry: RegistryDocument;
  /** StreamsView hides the stream name (it is the section header); ProjectsView shows it. */
  showStream?: boolean;
  onEdit: (project: Project) => void;
  /** Set on «По потокам» only: adds the drag-handle column and the drop events. */
  manualOrder?: ManualOrderControls;
  /**
   * F-28 («Все проекты», «Вручную» only): the project's GLOBAL position over
   * the full unfiltered list. Passed together with `onSendToTop` — their
   * presence is what renders the «№» column, on this page in this mode only.
   */
  position?: number | null;
  /** F-28 — the row's «наверх» (global position 1); lives in the drag-tools cluster. */
  onSendToTop?: (id: string) => void;
}

/**
 * One project row (F-08, F-15):
 *  - the at-risk marker is ALWAYS visible and clicking it filters instantly (F-07);
 *  - edit / delete / at-risk toggle appear on hover (and on keyboard focus) only;
 *  - quick fields inline: status, stream priority, category (dictionary),
 *    assignees, due date, hours — and NO stream select (F-15);
 *  - in «По потокам» + «Вручную» (F-25) a leading handle column: HTML5 native
 *    drag (old-app parity) with ↑/↓ buttons as the keyboard alternative;
 *  - in «Все проекты» + «Вручную» (F-28) the same handle column gains the
 *    «наверх» button, and a leading «№» cell shows the global position.
 */
export function ProjectRow({
  project,
  registry,
  showStream = false,
  onEdit,
  manualOrder,
  position,
  onSendToTop,
}: ProjectRowProps): ReactElement {
  const patch = usePatchProject();
  const remove = useDeleteProject();
  const { setFilters } = useFilters();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hoursText, setHoursText] = useState(project.hours === null ? '' : String(project.hours));

  const setField = (changes: ProjectFieldPatch) => patch.mutate({ id: project.id, body: { version: registry.version, set: changes } });

  /** The picker sends the WHOLE list — `assignments.set` replaces it (arch §3.2). */
  const setAssignments = (assignments: Assignment[]) =>
    patch.mutate({ id: project.id, body: { version: registry.version, set: {}, assignments: { set: assignments } } });

  const personById = new Map(registry.people.map((person) => [person.id, person]));
  const streamName = registry.streams.find((stream) => stream.id === project.streamId)?.name ?? project.streamId;
  const category = registry.categories.find((item) => item.id === project.categoryId);
  const overdue = isOverdue(project);

  const busy = patch.isPending || remove.isPending;
  const error = patch.error ?? remove.error;

  const dragEnabled = manualOrder !== undefined && manualOrder.enabled;
  const isDragging = dragEnabled && manualOrder?.dragId === project.id;
  const isDropTarget = dragEnabled && manualOrder?.overId === project.id && manualOrder.dragId !== project.id;

  /**
   * Row click opens the project card (owner request 2026-10-15, /projects and
   * /streams — matches the precedent on /people and /teams queues, F-18). The
   * pencil is its keyboard/hover twin; the row stays the primary affordance.
   * Interactive controls (select/input/button/a) take their own click — same
   * INTERACTIVE guard the row drag uses, so a click on the at-risk chip, the
   * status/priority/category selects, the date picker, the assignee chip's
   * «×», or the pencil still goes to that control. A drag in flight (in
   * «По потокам» + «Вручную») cancels the click — the drop is the affordance
   * there. Left button only.
   */
  const openFromRow = (event: MouseEvent<HTMLTableRowElement>) => {
    if (event.button !== 0) return;
    if (isDragging) return;
    if ((event.target as HTMLElement | null)?.closest(INTERACTIVE)) return;
    onEdit(project);
  };

  return (
    <tr
      className={[
        'project-row',
        priorityRowClass(project),
        isDragging ? 'project-row--dragging' : '',
        isDropTarget ? 'project-row--drop-target' : '',
        dragEnabled ? 'project-row--draggable' : 'project-row--clickable',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={`project-row-${project.id}`}
      onClick={openFromRow}
      aria-busy={busy || undefined}
      {...(manualOrder
        ? manualRowDragProps(project.id, manualOrder, {
            title: project.title,
            statusLabel: statusLabel(registry.statuses, project.status),
            priorityLabel: PROJECT_PRIORITY_LABELS[project.priority],
          })
        : {})}
    >
      {position !== undefined ? <td className="project-table__pos num">{position ?? '—'}</td> : null}
      {manualOrder ? (
        <DragHandleCell
          id={project.id}
          label={project.title}
          controls={manualOrder}
          busy={busy}
          className="project-row__drag"
          onSendToTop={onSendToTop}
        />
      ) : null}
      <td className="project-row__risk">
        {project.atRisk ? (
          <button
            type="button"
            className="risk-marker"
            aria-label="Показать только at-risk проекты"
            title="Требует рассмотрения — показать только at-risk"
            onClick={() => setFilters({ atRisk: true })}
          >
            <TriangleAlert size={16} aria-hidden="true" />
          </button>
        ) : (
          <span className="risk-marker risk-marker--off" aria-hidden="true" />
        )}
      </td>

      <td className="cell-primary project-row__title">
        <span className="cell-primary__text" title={project.title}>
          {project.title}
        </span>
        <span className="cell-sub" title={project.description || undefined}>
          {showStream ? `${streamName}${project.description ? ' — ' : ''}` : ''}
          {project.description}
        </span>
        {error instanceof Error ? (
          isConflict(error) ? (
            // Someone else saved first: the fresh document is already in the
            // cache (the hook heals it), so this row re-rendered from it.
            <span className="badge badge--warn" title={error.message}>
              Изменён кем-то другим — данные обновлены
            </span>
          ) : (
            <span className="badge badge--bad" role="alert" title={error.message}>
              Не сохранено
            </span>
          )
        ) : null}
      </td>

      <td>
        {/* Options come from the managed dictionary (F-01/F-23): dictionary
            order, current labels. A project whose stored id is missing from
            the dictionary (must not happen — deletion refuses while in use)
            still shows its id as the selected value. */}
        <select
          aria-label={`Статус: ${project.title}`}
          className="input input--compact row-input row-input--status"
          value={project.status}
          disabled={busy}
          onChange={(event) => setField({ status: event.target.value as Project['status'] })}
        >
          {statusOptions(registry.statuses).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </td>

      <td>
        <select
          aria-label={`Приоритет потока: ${project.title}`}
          className="input input--compact row-input row-input--priority"
          value={project.priority}
          disabled={busy}
          onChange={(event) => setField({ priority: event.target.value as Project['priority'] })}
        >
          {PROJECT_PRIORITIES.map((priority) => (
            <option key={priority} value={priority}>
              {PROJECT_PRIORITY_LABELS[priority]}
            </option>
          ))}
        </select>
      </td>

      <td>
        <select
          aria-label={`Категория: ${project.title}`}
          className="input input--compact row-input row-input--category"
          value={project.categoryId ?? ''}
          disabled={busy}
          onChange={(event) => setField({ categoryId: event.target.value || null })}
        >
          <option value="">—</option>
          {registry.categories.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
        {category ? null : <span className="sr-only">категория не задана</span>}
      </td>

      <td className="project-row__assignees">
        {project.assignments.map((assignment) => {
          const person = personById.get(assignment.personId);
          const name = person?.name ?? assignment.personId;
          return (
            <span
              key={assignment.personId}
              className="assignee-chip"
              title={`${name} · в личной очереди: ${PROJECT_PRIORITY_LABELS[assignment.personPriority]} · в очереди команды: ${PROJECT_PRIORITY_LABELS[assignment.teamPriority]}`}
            >
              <span className="assignee-chip__avatar" aria-hidden="true">
                {initials(name)}
              </span>
              <span className="assignee-chip__name">{name}</span>
              <button
                type="button"
                className="assignee-chip__remove"
                aria-label={`Убрать ${name} из исполнителей`}
                title={`Убрать ${name}`}
                disabled={busy}
                onClick={() =>
                  setAssignments(project.assignments.filter((item) => item.personId !== assignment.personId))
                }
              >
                <X size={12} aria-hidden="true" />
              </button>
            </span>
          );
        })}
        <button
          type="button"
          className="icon-btn icon-btn--sm"
          aria-label={`Добавить исполнителя: ${project.title}`}
          title="Добавить исполнителя из справочника"
          disabled={busy}
          onClick={() => setPickerOpen(true)}
        >
          <Plus size={14} aria-hidden="true" />
        </button>
      </td>

      <td className="project-row__date">
        <div className={overdue ? 'date-cell date-cell--overdue' : 'date-cell'}>
          <DatePicker
            id={`due-${project.id}`}
            label={`Срок до: ${project.title}`}
            value={project.dueDate}
            disabled={busy}
            className="input--compact row-input"
            onChange={(iso) => setField({ dueDate: iso })}
          />
          {overdue ? <span className="badge badge--bad">просрочено</span> : null}
        </div>
      </td>

      <td className="num project-row__hours">
        <input
          type="number"
          min={0}
          step={1}
          className="input input--compact row-input row-input--num"
          aria-label={`Трудозатраты, часов: ${project.title}`}
          title="Оценка на весь проект, часов"
          placeholder=""
          value={hoursText}
          disabled={busy}
          onChange={(event) => setHoursText(event.target.value)}
          onBlur={() => {
            const trimmed = hoursText.trim();
            const next = trimmed === '' ? null : Number(trimmed);
            if (next === null || (Number.isFinite(next) && next >= 0)) {
              if (next !== project.hours) setField({ hours: next });
            } else {
              setHoursText(project.hours === null ? '' : String(project.hours));
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
        />
      </td>

      <td className="num">
        <div className="row-actions project-row__actions">
          <button
            type="button"
            className="icon-btn icon-btn--sm"
            aria-label={project.atRisk ? `Снять at-risk: ${project.title}` : `Пометить at-risk: ${project.title}`}
            title={project.atRisk ? 'Снять флаг «Требует рассмотрения»' : 'Требует рассмотрения (at-risk)'}
            disabled={busy}
            aria-pressed={project.atRisk}
            onClick={() => setField({ atRisk: !project.atRisk })}
          >
            <TriangleAlert size={14} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="icon-btn icon-btn--sm"
            aria-label={`Редактировать: ${project.title}`}
            title="Редактировать проект"
            disabled={busy}
            onClick={() => onEdit(project)}
          >
            <Pencil size={14} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="icon-btn icon-btn--sm icon-btn--danger"
            aria-label={`Удалить: ${project.title}`}
            title="Удалить проект"
            disabled={busy}
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 size={14} aria-hidden="true" />
          </button>
        </div>
      </td>

      <PersonPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        people={registry.people}
        teams={registry.teams}
        exclude={new Set(project.assignments.map((a) => a.personId))}
        onPick={(person) =>
          setAssignments([
            ...project.assignments,
            {
              personId: person.id,
              personPriority: project.priority,
              teamPriority: project.priority,
              // A fresh assignment starts unordered in both manual queues.
              personPosition: null,
              teamPosition: null,
            },
          ])
        }
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Удалить проект?"
        busy={remove.isPending}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate({ id: project.id, version: registry.version }, { onSuccess: () => setConfirmDelete(false) })}
        description={<p>«{project.title}» будет удалён из реестра вместе с назначениями. Действие попадёт в аудит-лог.</p>}
      />
    </tr>
  );
}
